import { createFileRoute } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { CalendarPage, type CalendarPageSearch } from '@/features/calendar';

// Calendar is a public reader surface — guests see whichever events
// land in the `Everyone`-scoped slice (none in V1, but the wire shape
// supports it). Auth-only flows live behind the EventComposer (the
// route layer 401s on POST/PATCH/DELETE if the viewer isn't signed
// in), so we don't AuthGuard the page itself.
function CalendarRoute(): ReactNode {
  const search = Route.useSearch();
  return <CalendarPage search={search} />;
}

export const Route = createFileRoute('/calendar')({
  validateSearch,
  component: CalendarRoute,
});

const CALENDAR_VIEWS = new Set(['month', 'week', 'day', 'agenda']);
const CALENDAR_STATUSES = new Set(['scheduled', 'live', 'past', 'cancelled']);
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function validateSearch(raw: Record<string, unknown>): CalendarPageSearch {
  const out: CalendarPageSearch = {};
  if (typeof raw.view === 'string' && CALENDAR_VIEWS.has(raw.view)) {
    out.view = raw.view as CalendarPageSearch['view'];
  }
  if (typeof raw.date === 'string' && isValidDateKey(raw.date)) {
    out.date = raw.date;
  }
  if (typeof raw.group === 'string' && UUID_RE.test(raw.group)) {
    out.group = raw.group;
  }
  const groups = parseGroups(raw.groups);
  if (groups.length > 0) out.groups = groups;
  const kinds = parseKinds(raw.kinds);
  if (kinds.length > 0) out.kinds = kinds;
  const status = parseStatuses(raw.status);
  if (status.length > 0) out.status = status;
  return out;
}

function parseKinds(value: unknown): string[] {
  return parseCsvList(value);
}

function parseGroups(value: unknown): string[] {
  return parseCsvList(value).filter((entry) => UUID_RE.test(entry));
}

function parseStatuses(value: unknown): NonNullable<CalendarPageSearch['status']> {
  return parseCsvList(value).filter(
    (entry): entry is NonNullable<CalendarPageSearch['status']>[number] =>
      CALENDAR_STATUSES.has(entry),
  );
}

function isValidDateKey(value: string): boolean {
  if (!DATE_RE.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number) as [number, number, number];
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

function parseCsvList(value: unknown): string[] {
  const values = Array.isArray(value) ? value : [value];
  return [
    ...new Set(
      values.flatMap((entry): string[] => {
        if (typeof entry !== 'string') return [];
        return entry
          .split(',')
          .map((part) => part.trim())
          .filter((part) => part.length > 0);
      }),
    ),
  ];
}
